import React from 'react';
import { FormField } from '../../../components';

// Step navigation (Back / Next / Save) lives in the ConvertClient page header,
// top-right, not at the end of each step.
const PersonalInfo = ({ updateData, data }) => {
    const personal = data.personalDetails || {};

    const handleChange = (e) => {
        const { name, value } = e.target;
        updateData('personalDetails', { [name]: value });
    };

    return (
        <div>
            <h2 className="text-[22px] font-bold text-[#334155] dark:text-gray-200 mb-8">Personal details</h2>

            <div className="flex flex-col md:flex-row gap-10">
                {/* Left Column */}
                <div className="flex-1 flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="First Name"
                            name="first_name"
                            value={personal.first_name || ''}
                            onChange={handleChange}
                            placeholder="Enter first name"
                            required
                        />
                        <FormField
                            label="Middle Name"
                            name="middle_name"
                            value={personal.middle_name || ''}
                            onChange={handleChange}
                            placeholder="Enter middle name"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Surname"
                            name="surname"
                            value={personal.surname || ''}
                            onChange={handleChange}
                            placeholder="Enter surname"
                            required
                        />
                        <FormField
                            label="Preferred Name"
                            name="preferred_name"
                            value={personal.preferred_name || ''}
                            onChange={handleChange}
                            placeholder="How they like to be called"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Current Level of Care"
                            name="level_of_care"
                            value={personal.level_of_care || ''}
                            onChange={handleChange}
                            placeholder="e.g. Residential"
                        />
                        <FormField
                            label="Current GP Name"
                            name="gp_name"
                            value={personal.gp_name || ''}
                            onChange={handleChange}
                            placeholder="Enter GP name"
                        />
                    </div>

                    <FormField
                        label="Current GP Address"
                        name="gp_address"
                        type="textarea"
                        rows={2}
                        value={personal.gp_address || ''}
                        onChange={handleChange}
                        placeholder="Enter GP surgery address"
                    />

                    <FormField
                        label="GP Phone"
                        name="gp_phone"
                        value={personal.gp_phone || ''}
                        onChange={handleChange}
                        placeholder="e.g. 01632 960100 (shown on the emergency screen)"
                    />
                    
                    <FormField
                        label="Identity Mark / Features"
                        name="identity_marks"
                        value={personal.identity_marks || ''}
                        onChange={handleChange}
                        placeholder="e.g. Birthmark on left arm"
                    />

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Mobility"
                            name="mobility"
                            value={personal.mobility || ''}
                            onChange={handleChange}
                            placeholder="e.g. Independent"
                        />
                        <FormField
                            label="Continence"
                            name="continence"
                            value={personal.continence || ''}
                            onChange={handleChange}
                            placeholder="e.g. Independent"
                        />
                    </div>

                    <FormField
                        label="Any Known allergy"
                        name="known_allergies"
                        type="textarea"
                        rows={2}
                        value={personal.known_allergies || ''}
                        onChange={handleChange}
                        placeholder="List all allergies or 'None'"
                    />
                </div>

                {/* Vertical Dashed Divider */}
                <div className="hidden md:block w-px border-l-2 border-dashed border-gray-200 dark:border-gray-800"></div>

                {/* Right Column */}
                <div className="flex-1 flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Date of birth"
                            name="dob"
                            type="date"
                            value={personal.dob || ''}
                            onChange={handleChange}
                            required
                        />
                        <FormField
                            label="Date of Admission"
                            name="date_of_admission"
                            type="date"
                            value={personal.date_of_admission || ''}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Gender"
                            name="gender"
                            type="select"
                            value={personal.gender || ''}
                            onChange={handleChange}
                            placeholder="Select Gender"
                            options={[
                                { label: 'Male', value: 'Male' },
                                { label: 'Female', value: 'Female' },
                                { label: 'Other', value: 'Other' }
                            ]}
                        />
                        <FormField
                            label="First Language"
                            name="first_language"
                            value={personal.first_language || ''}
                            onChange={handleChange}
                            placeholder="e.g. English"
                        />
                    </div>

                    {/* Room and unit were one box, so a record could hold either
                        but never both. They are stored separately now —
                        allocated_room_unit is the ROOM (see models/Residence.js). */}
                    <div className="grid grid-cols-3 gap-5">
                        <FormField
                            label="Allocated Room"
                            name="allocated_room_unit"
                            value={personal.allocated_room_unit || ''}
                            onChange={handleChange}
                            placeholder="e.g. 104"
                        />
                        <FormField
                            label="Unit"
                            name="allocated_unit"
                            value={personal.allocated_unit || ''}
                            onChange={handleChange}
                            placeholder="e.g. Bluebell"
                        />
                        <FormField
                            label="Floor Number"
                            name="floor_number"
                            value={personal.floor_number || ''}
                            onChange={handleChange}
                            placeholder="e.g. 1st Floor"
                        />
                    </div>

                    <FormField
                        label="NHS Number"
                        name="nhs_number"
                        value={personal.nhs_number || ''}
                        onChange={handleChange}
                        placeholder="XXX-XXX-XXXX"
                    />

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Current Weight (kg)"
                            name="current_weight"
                            value={personal.current_weight || ''}
                            onChange={handleChange}
                            placeholder="e.g. 70"
                        />
                        <FormField
                            label="Current Height (cm)"
                            name="current_height"
                            value={personal.current_height || ''}
                            onChange={handleChange}
                            placeholder="e.g. 170"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Marital Status"
                            name="marital_status"
                            value={personal.marital_status || ''}
                            onChange={handleChange}
                            placeholder="e.g. Married"
                        />
                        <FormField
                            label="Employment status"
                            name="employment_status"
                            value={personal.employment_status || ''}
                            onChange={handleChange}
                            placeholder="e.g. Retired"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Ethnicity"
                            name="ethnicity"
                            value={personal.ethnicity || ''}
                            onChange={handleChange}
                            placeholder="e.g. White British"
                        />
                        <FormField
                            label="Nationality"
                            name="nationality"
                            value={personal.nationality || ''}
                            onChange={handleChange}
                            placeholder="e.g. British"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField
                            label="Religion"
                            name="religion"
                            value={personal.religion || ''}
                            onChange={handleChange}
                            placeholder="e.g. Christian"
                        />
                        <FormField
                            label="Type of stay"
                            name="type_of_stay"
                            value={personal.type_of_stay || ''}
                            onChange={handleChange}
                            placeholder="e.g. Permanent"
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PersonalInfo;